/**
 * Client-side find-on-page matching for the in-table search bar.
 *
 * This powers the browser-style "find in view" experience: it does NOT filter
 * rows — it locates matching substrings within the cell text the user already
 * sees so they can be highlighted and navigated. Matching runs over loaded
 * rows only (the grid is virtualized and server-paginated); rows not yet
 * fetched are not searched.
 */
import type { DataRecord as TableRecord } from '../../records/models'
import type { Column } from '@features/columns/models'
import { resolveCellText } from '../utils/cellText'

export type MatchType = 'contains' | 'equals' | 'startsWith' | 'endsWith'
export type SearchOrder = 'rows' | 'columns'

export interface MatchMode {
  /** How the query is positioned within the cell text. */
  type: MatchType
  /** When true, comparison is case-sensitive. */
  caseSensitive: boolean
  /** When true, matches must sit on word boundaries. */
  wholeWord: boolean
  /**
   * When true, match the query as strict literal text. When false, diacritics
   * are folded so e.g. "cafe" matches "café".
   */
  exactText: boolean
  /** Spatial traversal order for mixed column-header + body-value results. */
  order: SearchOrder
}

export const DEFAULT_MATCH_MODE: MatchMode = {
  type: 'contains',
  caseSensitive: false,
  wholeWord: false,
  exactText: false,
  order: 'rows',
}

/**
 * Length-preserving diacritic fold: each mapped character maps to exactly one
 * base character, so match offsets stay valid against the original text.
 */
const DIACRITIC_MAP: Record<string, string> = {
  à: 'a',
  á: 'a',
  â: 'a',
  ã: 'a',
  ä: 'a',
  å: 'a',
  ā: 'a',
  ă: 'a',
  ą: 'a',
  ç: 'c',
  ć: 'c',
  č: 'c',
  è: 'e',
  é: 'e',
  ê: 'e',
  ë: 'e',
  ē: 'e',
  ė: 'e',
  ę: 'e',
  ì: 'i',
  í: 'i',
  î: 'i',
  ï: 'i',
  ī: 'i',
  į: 'i',
  ñ: 'n',
  ń: 'n',
  ò: 'o',
  ó: 'o',
  ô: 'o',
  õ: 'o',
  ö: 'o',
  ø: 'o',
  ō: 'o',
  ù: 'u',
  ú: 'u',
  û: 'u',
  ü: 'u',
  ū: 'u',
  ý: 'y',
  ÿ: 'y',
  ž: 'z',
  ź: 'z',
  ż: 'z',
}
const DIACRITIC_RE = /[àáâãäåāăąçćčèéêëēėęìíîïīįñńòóôõöøōùúûüūýÿžźż]/g

function foldDiacritics(value: string): string {
  return value.replace(DIACRITIC_RE, (ch) => DIACRITIC_MAP[ch] ?? ch)
}

/** A matched substring within a cell's text, as half-open [start, end). */
export interface MatchRange {
  start: number
  end: number
}

export type TableSearchResultKind = 'record' | 'value' | 'column'
export type TableSearchScope = 'all' | 'records' | 'values' | 'columns'
export type TableSearchResultScope = Exclude<TableSearchScope, 'all'>

export interface TableSearchResult {
  id: string
  kind: TableSearchResultKind
  label: string
  detail: string
  /** False when the result exists in the table but is outside the current view. */
  inView?: boolean
  /** False when the result cannot be reached by find navigation in the loaded grid. */
  findable?: boolean
  recordId?: string
  rowIndex?: number
  columnId?: string
  columnLabel?: string
  value?: string
}

export interface TableSearchResultsByScope {
  all: TableSearchResult[]
  records: TableSearchResult[]
  values: TableSearchResult[]
  columns: TableSearchResult[]
}

export interface TableSearchPreview {
  results: TableSearchResultsByScope
  counts: Record<TableSearchResultScope, number>
  countSuffixes: Partial<Record<TableSearchResultScope, string>>
}

export const EMPTY_TABLE_SEARCH_RESULTS: TableSearchResultsByScope = {
  all: [],
  records: [],
  values: [],
  columns: [],
}

export const EMPTY_TABLE_SEARCH_PREVIEW: TableSearchPreview = {
  results: EMPTY_TABLE_SEARCH_RESULTS,
  counts: { records: 0, values: 0, columns: 0 },
  countSuffixes: {},
}

export function getResultsForScope(
  results: TableSearchResultsByScope,
  scope: TableSearchScope
): TableSearchResult[] {
  return results[scope]
}

const WORD_CHAR = /[\p{L}\p{N}_]/u

/** Whether the character at `index` (or absence of one) is a word boundary edge. */
function isBoundary(text: string, index: number): boolean {
  if (index < 0 || index >= text.length) return true
  return !WORD_CHAR.test(text[index])
}

function satisfiesWholeWord(text: string, start: number, end: number): boolean {
  return isBoundary(text, start - 1) && isBoundary(text, end)
}

/** Lower/fold the query once, the way each cell's haystack will be prepared. */
function prepareNeedle(query: string, mode: MatchMode): string {
  const needle = mode.caseSensitive ? query : query.toLowerCase()
  return mode.exactText ? needle : foldDiacritics(needle)
}

/**
 * Find every range in `text` that matches `query` under the given mode.
 * Returns an empty array when there is no query or no match. The ranges index
 * into the original `text` so callers can slice it for highlighting.
 */
export function findMatchRanges(text: string, query: string, mode: MatchMode): MatchRange[] {
  if (!query) return []
  return findMatchRangesWithNeedle(text, prepareNeedle(query, mode), mode)
}

/**
 * Match core against a pre-prepared needle — bulk scans prepare the needle
 * once per pass instead of once per cell.
 */
function findMatchRangesWithNeedle(text: string, needle: string, mode: MatchMode): MatchRange[] {
  if (text.length === 0) return []

  let haystack = mode.caseSensitive ? text : text.toLowerCase()
  if (!mode.exactText) {
    haystack = foldDiacritics(haystack)
  }
  if (needle.length === 0) return []

  const ranges: MatchRange[] = []

  switch (mode.type) {
    case 'equals': {
      if (haystack === needle && (!mode.wholeWord || satisfiesWholeWord(text, 0, text.length))) {
        ranges.push({ start: 0, end: text.length })
      }
      break
    }
    case 'startsWith': {
      if (
        haystack.startsWith(needle) &&
        (!mode.wholeWord || satisfiesWholeWord(text, 0, needle.length))
      ) {
        ranges.push({ start: 0, end: needle.length })
      }
      break
    }
    case 'endsWith': {
      if (
        haystack.endsWith(needle) &&
        (!mode.wholeWord || satisfiesWholeWord(text, text.length - needle.length, text.length))
      ) {
        ranges.push({ start: text.length - needle.length, end: text.length })
      }
      break
    }
    case 'contains':
    default: {
      let from = 0
      while (from <= haystack.length - needle.length) {
        const at = haystack.indexOf(needle, from)
        if (at === -1) break
        const end = at + needle.length
        if (!mode.wholeWord || satisfiesWholeWord(text, at, end)) {
          ranges.push({ start: at, end })
        }
        from = at + needle.length
      }
      break
    }
  }

  return ranges
}

/** Whether the cell text matches the query under the given mode. */
export function cellMatches(text: string, query: string, mode: MatchMode): boolean {
  return findMatchRanges(text, query, mode).length > 0
}

// ─── Prepared matching + per-record match caching ─────────────────

/**
 * Everything a bulk scan needs to match one query: the needle is prepared
 * once, and the signature keys caches. `order` is excluded — it only affects
 * result assembly, never whether a cell matches.
 */
export interface TableSearchMatchContext {
  query: string
  mode: MatchMode
  signature: string
  needle: string
}

export function createTableSearchMatchContext(
  query: string,
  mode: MatchMode
): TableSearchMatchContext {
  const signature = [
    mode.type,
    mode.caseSensitive ? 1 : 0,
    mode.wholeWord ? 1 : 0,
    mode.exactText ? 1 : 0,
    query,
  ].join('\u0000')

  return { query, mode, signature, needle: prepareNeedle(query, mode) }
}

/** Context-based `cellMatches` — skips the per-cell needle preparation. */
export function cellMatchesContext(text: string, context: TableSearchMatchContext): boolean {
  return findMatchRangesWithNeedle(text, context.needle, context.mode).length > 0
}

/** One matched cell of a record: the column and its resolved display text. */
export interface RecordCellMatch {
  column: Column
  text: string
}

interface RecordMatchCacheEntry {
  signature: string
  columnsToken: number
  matches: RecordCellMatch[]
}

const recordMatchCache = new WeakMap<TableRecord, RecordMatchCacheEntry>()
const columnsTokenByArray = new WeakMap<Column[], number>()
let nextColumnsToken = 1

/** Identity token for a columns array, so cache entries can tell column-set changes apart. */
function getColumnsToken(columns: Column[]): number {
  let token = columnsTokenByArray.get(columns)
  if (token == null) {
    token = nextColumnsToken
    nextColumnsToken += 1
    columnsTokenByArray.set(columns, token)
  }
  return token
}

/**
 * The matched cells of one record for the given context, cached by record
 * object identity. Record objects are immutable snapshots — column-window
 * merges produce new objects — so data changes invalidate automatically, and
 * the signature + columns token invalidate on query/mode/column-set changes.
 * This keeps page appends and jump-row arrivals from re-matching records that
 * were already scanned for the current query.
 */
export function getRecordCellMatches(
  record: TableRecord,
  columns: Column[],
  context: TableSearchMatchContext
): RecordCellMatch[] {
  const columnsToken = getColumnsToken(columns)
  const cached = recordMatchCache.get(record)
  if (cached && cached.signature === context.signature && cached.columnsToken === columnsToken) {
    return cached.matches
  }

  const matches: RecordCellMatch[] = []
  for (const column of columns) {
    const raw = record.data[column.name] ?? record.data[column.id]
    const text = resolveCellText(raw, column)
    if (!text || !cellMatchesContext(text, context)) continue

    matches.push({ column, text })
  }
  recordMatchCache.set(record, { signature: context.signature, columnsToken, matches })
  return matches
}
